import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './CallTree.css';

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const howItWorks = [
  { step: '01', icon: '📋', title: 'Register Online', desc: 'Visit calltree.in and register your participation. Fill in your details and choose your tree dedication.' },
  { step: '02', icon: '💚', title: 'Dedicate a Tree', desc: 'Dedicate the tree to someone you love — on their birthday, anniversary, or any special occasion.' },
  { step: '03', icon: '🌱', title: 'We Plant Your Tree', desc: 'In coordination with the Forest Department, your tree is planted at a certified location.' },
  { step: '04', icon: '📍', title: 'Track & Share', desc: 'Receive your tree\'s location details. Share the memory with your loved one and the world.' },
];

const campaigns = [
  {
    icon: '❤️',
    title: 'Plant a Love',
    subtitle: 'Valentine\'s Day Campaign',
    desc: 'Gift a tree on Valentine\'s Day. Love and air are the purest of its nature — come, let\'s get cleansed together.',
    color: '#e91e8c',
    bg: '#fff0f7',
  },
  {
    icon: '🎂',
    title: 'Birthday Tree',
    subtitle: 'Celebrate with Nature',
    desc: 'Mark every birthday with a tree that grows alongside your loved one — a gift that gives back to the planet.',
    color: '#ff6b2b',
    bg: '#fff5f0',
  },
  {
    icon: '💍',
    title: 'Anniversary Grove',
    subtitle: 'A Living Milestone',
    desc: 'Celebrate your anniversary with a grove of trees — a living testament to your enduring love and commitment.',
    color: '#9c27b0',
    bg: '#f5f0ff',
  },
  {
    icon: '🕊️',
    title: 'Memorial Tree',
    subtitle: 'Honor a Memory',
    desc: 'Plant a tree in honor of someone who has passed — their memory lives on in every leaf and every breath of air.',
    color: '#1a5c2e',
    bg: '#f0faf4',
  },
];

const forestPartner = [
  '✅ Official Forest Department collaboration',
  '✅ Government certified planting sites',
  '✅ Professionally maintained saplings',
  '✅ GPS tracked plantation zones',
  '✅ Annual growth reports',
  '✅ Biodiversity-focused species selection',
];

export default function CallTree() {
  return (
    <div className="calltree-page">
      {/* HERO */}
      <section className="page-hero calltree-hero">
        <div className="calltree-hero__bg" />
        <div className="page-hero__overlay" />
        <div className="container page-hero__content">
          <div className="calltree-hero__logos">
            <div className="calltree-logo-badge">🌍 Y2K Global Nexus</div>
            <div className="calltree-logo-divider">×</div>
            <div className="calltree-logo-badge calltree-logo-badge--green">🌲 CallTree</div>
          </div>
          <h1 className="page-hero__title calltree-hero__title">
            calltree.in
          </h1>
          <p className="page-hero__sub">
            India's most meaningful tree-planting platform. Every tree is a memory.
            Every memory, ever green. In partnership with the Forest Department.
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '32px' }}>
            <Link to="/contact" className="btn-gold">🌱 Plant Your Tree Now</Link>
            <a href="#how-it-works" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white' }}>
              How It Works
            </a>
          </div>
        </div>
      </section>

      {/* PLANT A LOVE POEM */}
      <section className="ct-poem section-padding">
        <div className="container">
          <FadeSection>
            <div className="ct-poem__inner">
              <div className="ct-poem__content">
                <span className="tag">The Inspiration</span>
                <h2 className="section-title">Plant a Love</h2>
                <div className="ct-poem__lines">
                  <p>This Life is full of <em>Memories</em></p>
                  <p>Memories are made with beautiful <em>Moments</em></p>
                  <p>Moments can be created —</p>
                  <p>Present him / her your beautiful Memories</p>
                  <p className="ct-poem__highlight">Is there any best present you could give her / him better than your memory?</p>
                  <p>Memories are <em>ever Green</em></p>
                  <p>Greener trees hold <em>Green Memories</em></p>
                  <p>When you present a Green tree — it holds your green Memories</p>
                  <p className="ct-poem__highlight">You Live-in her / his Memories</p>
                  <p>This Nation knowing you Love</p>
                  <p>The air is <em>Purified</em></p>
                  <p>Love and air are the purest of its nature</p>
                  <p className="ct-poem__cta-line">
                    Come on — Lets get cleansed<br />
                    <strong>On this Valentine's Day</strong><br />
                    Register your participation and share your Love
                  </p>
                </div>
              </div>
              <div className="ct-poem__visual">
                <div className="ct-poem-card">
                  <div className="ct-poem-card__tree">🌲</div>
                  <h3>Plant a Love Tree</h3>
                  <p>This Valentine's Day</p>
                  <div className="ct-poem-card__logos">
                    <div className="ct-poem-card__logo">
                      <span>🌍</span>
                      <strong>Y2K Global Nexus</strong>
                    </div>
                    <div className="ct-poem-card__logo">
                      <span>🌲</span>
                      <strong>Call Tree Team</strong>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '24px' }}>
                    Register Now
                  </Link>
                </div>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="ct-how section-padding" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span className="tag">Process</span>
              <h2 className="section-title">How CallTree Works</h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                From registration to a living tree — simple, transparent, impactful.
              </p>
            </div>
          </FadeSection>
          <div className="ct-how__steps">
            {howItWorks.map(({ step, icon, title, desc }, i) => (
              <FadeSection key={i} delay={i * 100}>
                <div className="ct-step">
                  <div className="ct-step__number">{step}</div>
                  <div className="ct-step__icon">{icon}</div>
                  <h3 className="ct-step__title">{title}</h3>
                  <p className="ct-step__desc">{desc}</p>
                  {i < howItWorks.length - 1 && <div className="ct-step__connector" />}
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* CAMPAIGNS */}
      <section className="ct-campaigns section-padding">
        <div className="container">
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span className="tag">Campaigns</span>
              <h2 className="section-title">Ways to Plant a Memory</h2>
            </div>
          </FadeSection>
          <div className="ct-campaigns__grid">
            {campaigns.map(({ icon, title, subtitle, desc, color, bg }, i) => (
              <FadeSection key={i} delay={i * 80}>
                <div className="ct-campaign-card" style={{ '--campaign-color': color, '--campaign-bg': bg }}>
                  <div className="ct-campaign-card__icon">{icon}</div>
                  <div className="ct-campaign-card__subtitle">{subtitle}</div>
                  <h3 className="ct-campaign-card__title">{title}</h3>
                  <p className="ct-campaign-card__desc">{desc}</p>
                  <Link to="/contact" className="ct-campaign-card__link">
                    Participate →
                  </Link>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* FOREST DEPT PARTNERSHIP */}
      <section className="ct-forest section-padding">
        <div className="container">
          <div className="ct-forest__inner">
            <FadeSection>
              <div className="ct-forest__content">
                <span className="tag">Official Partnership</span>
                <h2 className="section-title">Forest Department<br />Collaboration</h2>
                <p className="section-subtitle" style={{ marginBottom: '32px' }}>
                  Every tree planted through CalllTree.in is done in official coordination with
                  the Forest Department — ensuring quality, certification, and long-term care.
                </p>
                <ul className="ct-forest__list">
                  {forestPartner.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-primary" style={{ marginTop: '32px', display: 'inline-flex' }}>
                  Plant With Us
                </Link>
              </div>
            </FadeSection>
            <FadeSection delay={200}>
              <div className="ct-forest__badge">
                <div className="ct-forest__badge-inner">
                  <div className="ct-forest__badge-icon">🏛️</div>
                  <h3>Official Forest Department</h3>
                  <p>Certified Partnership</p>
                  <div className="ct-forest__cert">
                    <span>🌳 Verified Sites</span>
                    <span>📋 Govt. Certified</span>
                    <span>🌍 Global Scale</span>
                    <span>💚 Y2K Assured</span>
                  </div>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ct-cta">
        <div className="container">
          <FadeSection>
            <div className="ct-cta__inner">
              <h2>Start Your Green Memory Today</h2>
              <p>Register on calltree.in and plant a tree that will outlive us all.</p>
              <Link to="/contact" className="btn-gold" style={{ fontSize: '1.1rem', padding: '16px 40px' }}>
                🌱 Register on CallTree
              </Link>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
}
